import {
  ATTACH_KINDS,
  EQUIP_SLOTS,
  WEAPON_EQUIP_SLOTS,
  type AttachKind,
  type EquipSlot,
  type WeaponEquipSlot,
} from "../items/types.js";
import type { Rotation } from "../inventory/grid.js";
import type { AreaId, InventoryOp, TargetRef } from "../inventory/ops.js";
import type { Vec3 } from "../math/vec3.js";
import {
  BTN_ALL_MASK,
  MAX_CMDS_PER_INPUT,
  MAX_NAME_LENGTH,
  type ClientMsg,
  type InputCmd,
} from "./messages.js";

/**
 * Hand-written runtime validation for everything from a client. No schema
 * library; the output is always a freshly built object so extra and inherited
 * fields are dropped. Shape, type, finiteness and range are all checked.
 */

export const MAX_PITCH = Math.PI / 2;
/** Beyond this a yaw is nonsense and the message is dropped outright. */
const MAX_ABS_YAW = 1e6;
const MAX_TOKEN_LENGTH = 256;
const MAX_SEQ = Number.MAX_SAFE_INTEGER;

/** Any position component beyond this is nonsense no map could produce. */
const MAX_ABS_COORD = 1e6;

/**
 * A fire direction must arrive unit-length: generous for float32 rounding,
 * tight enough that a scaled vector — which would hand the sender arbitrary
 * range — is rejected.
 */
const MIN_DIR_LENGTH = 0.99;
const MAX_DIR_LENGTH = 1.01;

/**
 * Longest instance / container / world-item id. Ids are server-minted and
 * short; the bound keeps a client from making the server hash megabyte keys.
 */
export const MAX_ID_LENGTH = 48;

/**
 * Longest CONTENT id (trader, item def). Larger than `MAX_ID_LENGTH` because
 * these are authored; whether the id names anything is `getTrader` /
 * `getItemDef`'s answer.
 */
export const MAX_CONTENT_ID_LENGTH = 64;

/**
 * Longest account username — the registration rule's upper bound, named for the
 * client's input field.
 */
export const MAX_USERNAME_LENGTH = 24;

/**
 * Account username rule: 3–24 of letters, digits, underscore, dot, hyphen. A
 * MIRROR of `server/src/auth/httpAuth.ts` (nothing in `shared/` may import the
 * server); a name that could never have been registered is refused at the
 * wire. Says nothing about whether the account exists or is yours. Case is
 * preserved; the server matches case-insensitively.
 */
const USERNAME_RE = /^[A-Za-z0-9_.-]{3,24}$/;

/** True when `x` is a string that could be a registered username. */
export function isValidUsername(x: unknown): x is string {
  return typeof x === "string" && USERNAME_RE.test(x);
}

/**
 * Largest grid coordinate accepted — absurd headroom, so a coordinate can never
 * be a loop counter or allocation size; `placementProblem` enforces the real
 * grid.
 */
const MAX_GRID_COORD = 64;

/** Largest split count accepted. Way above any item's `stackMax`. */
const MAX_SPLIT_COUNT = 100000;

/**
 * Most instances one `questTurnIn` may name. Headroom, not a limit anybody
 * meets — the list is looked up id by id.
 */
const MAX_TURN_IN_IIDS = 100;

/**
 * The closed set of TOP-LEVEL areas an op may name. `"inside"` is the other
 * `TargetRef` variant and carries an `iid` instead.
 */
const AREA_IDS: readonly AreaId[] = ["pockets", "rig", "backpack", "container"];

function isPlainObject(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

function isFiniteNumber(x: unknown): x is number {
  return typeof x === "number" && Number.isFinite(x);
}

function isSafeInt(x: unknown): x is number {
  return typeof x === "number" && Number.isSafeInteger(x);
}

/** Rejects control characters, which would otherwise mangle any UI/logging. */
function isCleanString(x: unknown, maxLen: number): x is string {
  if (typeof x !== "string") return false;
  if (x.length > maxLen) return false;
  for (let i = 0; i < x.length; i++) {
    const c = x.charCodeAt(i);
    if (c < 0x20 || c === 0x7f) return false;
  }
  return true;
}

/** Validates a wire Vec3 into a FRESH copy; `maxAbs` bounds each component. */
function validateVec3(x: unknown, maxAbs: number): Vec3 | null {
  if (!isPlainObject(x)) return null;
  const { x: vx, y: vy, z: vz } = x;
  if (!isFiniteNumber(vx) || vx > maxAbs || vx < -maxAbs) return null;
  if (!isFiniteNumber(vy) || vy > maxAbs || vy < -maxAbs) return null;
  if (!isFiniteNumber(vz) || vz > maxAbs || vz < -maxAbs) return null;
  return { x: vx, y: vy, z: vz };
}

export function clampPitch(pitch: number): number {
  if (pitch > MAX_PITCH) return MAX_PITCH;
  if (pitch < -MAX_PITCH) return -MAX_PITCH;
  return pitch;
}

/** Validates a single input command, returning a sanitized copy or `null`. */
export function validateInputCmd(x: unknown): InputCmd | null {
  if (!isPlainObject(x)) return null;

  const { seq, buttons, yaw, pitch } = x;

  if (!isSafeInt(seq) || seq < 0 || seq > MAX_SEQ) return null;
  if (!isSafeInt(buttons) || buttons < 0) return null;
  if ((buttons & ~BTN_ALL_MASK) !== 0) return null;
  if (!isFiniteNumber(yaw) || yaw > MAX_ABS_YAW || yaw < -MAX_ABS_YAW) return null;
  if (!isFiniteNumber(pitch)) return null;

  return { seq, buttons, yaw, pitch: clampPitch(pitch) };
}

/** A non-negative small integer usable as a grid coordinate. */
function isGridCoord(x: unknown): x is number {
  return isSafeInt(x) && x >= 0 && x <= MAX_GRID_COORD;
}

/**
 * Validates a `TargetRef` into a fresh copy or `null`. `"inside"` REQUIRES a
 * clean `iid`; the four areas must not carry one (it is simply not copied).
 * Whether the host exists or is a container is `applyInventoryOp`'s answer.
 */
function validateTargetRef(x: unknown): TargetRef | null {
  if (!isPlainObject(x)) return null;
  const area = x["area"];
  if (typeof area !== "string") return null;
  if (!isGridCoord(x["x"]) || !isGridCoord(x["y"])) return null;
  const rot = x["rot"];
  if (rot !== 0 && rot !== 1) return null;

  if (area === "inside") {
    const iid = x["iid"];
    if (!isCleanString(iid, MAX_ID_LENGTH) || iid.length === 0) return null;
    return { area: "inside", iid, x: x["x"], y: x["y"], rot: rot as Rotation };
  }
  if (!AREA_IDS.includes(area as AreaId)) return null;
  return { area: area as AreaId, x: x["x"], y: x["y"], rot: rot as Rotation };
}

/**
 * The OPTIONAL `hostIid` on `attach`/`detach`. Three answers: absent (the gun
 * itself), present and clean, or `null` for a malformed one — a corrupt id must
 * not default to the gun.
 */
function validateHostIid(x: unknown): { hostIid?: string } | null {
  if (x === undefined) return {};
  if (!isCleanString(x, MAX_ID_LENGTH) || x.length === 0) return null;
  return { hostIid: x };
}

/**
 * Validates an `InventoryOp` deeply, rebuilding every union member field by
 * field. Exported for the server's own tests and the audit log. Existence, fit
 * and area are `applyInventoryOp`'s domain; this proves the SHAPE.
 */
export function validateInventoryOp(x: unknown): InventoryOp | null {
  if (!isPlainObject(x)) return null;
  if (typeof x["t"] !== "string") return null;

  switch (x["t"]) {
    case "move": {
      const iid = x["iid"];
      if (!isCleanString(iid, MAX_ID_LENGTH) || iid.length === 0) return null;
      const to = validateTargetRef(x["to"]);
      if (to === null) return null;
      return { t: "move", iid, to };
    }
    case "equip": {
      const iid = x["iid"];
      if (!isCleanString(iid, MAX_ID_LENGTH) || iid.length === 0) return null;
      const slot = x["slot"];
      if (typeof slot !== "string" || !EQUIP_SLOTS.includes(slot as EquipSlot)) return null;
      return { t: "equip", iid, slot: slot as EquipSlot };
    }
    case "unequip": {
      const slot = x["slot"];
      if (typeof slot !== "string" || !EQUIP_SLOTS.includes(slot as EquipSlot)) return null;
      const to = validateTargetRef(x["to"]);
      if (to === null) return null;
      return { t: "unequip", slot: slot as EquipSlot, to };
    }
    case "reslot": {
      // Two weapon slots only — a rig cannot be reslotted. Same-slot is
      // `applyInventoryOp`'s answer.
      const from = x["from"];
      if (typeof from !== "string" || !WEAPON_EQUIP_SLOTS.includes(from as WeaponEquipSlot)) {
        return null;
      }
      const to = x["to"];
      if (typeof to !== "string" || !WEAPON_EQUIP_SLOTS.includes(to as WeaponEquipSlot)) {
        return null;
      }
      return { t: "reslot", from: from as WeaponEquipSlot, to: to as WeaponEquipSlot };
    }
    case "split": {
      const iid = x["iid"];
      if (!isCleanString(iid, MAX_ID_LENGTH) || iid.length === 0) return null;
      const count = x["count"];
      if (!isSafeInt(count) || count < 1 || count > MAX_SPLIT_COUNT) return null;
      const to = validateTargetRef(x["to"]);
      if (to === null) return null;
      return { t: "split", iid, count, to };
    }
    // One shape: a source and a destination iid. What they name is
    // `applyInventoryOp`'s answer.
    case "merge":
    case "load": {
      const iid = x["iid"];
      if (!isCleanString(iid, MAX_ID_LENGTH) || iid.length === 0) return null;
      const ontoIid = x["ontoIid"];
      if (!isCleanString(ontoIid, MAX_ID_LENGTH) || ontoIid.length === 0) return null;
      return x["t"] === "merge" ? { t: "merge", iid, ontoIid } : { t: "load", iid, ontoIid };
    }
    // One shape: a single iid.
    case "drop":
    case "unload": {
      const iid = x["iid"];
      if (!isCleanString(iid, MAX_ID_LENGTH) || iid.length === 0) return null;
      return x["t"] === "drop" ? { t: "drop", iid } : { t: "unload", iid };
    }
    case "attach": {
      const iid = x["iid"];
      if (!isCleanString(iid, MAX_ID_LENGTH) || iid.length === 0) return null;
      const weaponIid = x["weaponIid"];
      if (!isCleanString(weaponIid, MAX_ID_LENGTH) || weaponIid.length === 0) return null;
      const slot = x["slot"];
      if (typeof slot !== "string" || !ATTACH_KINDS.includes(slot as AttachKind)) return null;
      const host = validateHostIid(x["hostIid"]);
      if (host === null) return null;
      // Whether the ids name the same thing is `applyInventoryOp`'s problem.
      return host.hostIid === undefined
        ? { t: "attach", iid, weaponIid, slot: slot as AttachKind }
        : { t: "attach", iid, weaponIid, slot: slot as AttachKind, hostIid: host.hostIid };
    }
    case "detach": {
      const weaponIid = x["weaponIid"];
      if (!isCleanString(weaponIid, MAX_ID_LENGTH) || weaponIid.length === 0) return null;
      const slot = x["slot"];
      if (typeof slot !== "string" || !ATTACH_KINDS.includes(slot as AttachKind)) return null;
      const to = validateTargetRef(x["to"]);
      if (to === null) return null;
      const host = validateHostIid(x["hostIid"]);
      if (host === null) return null;
      return host.hostIid === undefined
        ? { t: "detach", weaponIid, slot: slot as AttachKind, to }
        : { t: "detach", weaponIid, slot: slot as AttachKind, to, hostIid: host.hostIid };
    }
    default:
      return null;
  }
}

/**
 * Validates any client→server message into a sanitized `ClientMsg`, or `null`.
 * Never throws.
 */
export function validateClientMsg(x: unknown): ClientMsg | null {
  if (!isPlainObject(x)) return null;
  if (typeof x["t"] !== "string") return null;

  switch (x["t"]) {
    case "hello": {
      const protocol = x["protocol"];
      if (!isSafeInt(protocol) || protocol < 0 || protocol > 0xffff) return null;
      const name = x["name"];
      if (!isCleanString(name, MAX_NAME_LENGTH) || name.length === 0) return null;
      const token = x["token"];
      if (!isCleanString(token, MAX_TOKEN_LENGTH)) return null;
      return { t: "hello", protocol, name, token };
    }

    case "input": {
      const cmds = x["cmds"];
      if (!Array.isArray(cmds)) return null;
      if (cmds.length < 1 || cmds.length > MAX_CMDS_PER_INPUT) return null;
      const out: InputCmd[] = [];
      for (let i = 0; i < cmds.length; i++) {
        const cmd = validateInputCmd(cmds[i]);
        if (cmd === null) return null;
        out.push(cmd);
      }
      return { t: "input", cmds: out };
    }

    case "weaponSelect": {
      const slot = x["slot"];
      if (typeof slot !== "string" || !WEAPON_EQUIP_SLOTS.includes(slot as WeaponEquipSlot)) {
        return null;
      }
      return { t: "weaponSelect", slot: slot as WeaponEquipSlot };
    }

    case "ping": {
      const cNow = x["cNow"];
      if (!isFiniteNumber(cNow)) return null;
      return { t: "ping", cNow };
    }

    case "fire": {
      // Both ticks are client CLAIMS, bounded for sanity; plausibility is the
      // server's.
      const tick = x["tick"];
      if (!isSafeInt(tick) || tick < 0) return null;
      const renderTick = x["renderTick"];
      if (!isSafeInt(renderTick) || renderTick < 0) return null;

      const origin = validateVec3(x["origin"], MAX_ABS_COORD);
      if (origin === null) return null;

      // `dir` is bounded by the unit check below, so the component bound is
      // only there to keep the length maths away from overflow.
      const dir = validateVec3(x["dir"], MAX_ABS_COORD);
      if (dir === null) return null;
      const len = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
      if (len < MIN_DIR_LENGTH || len > MAX_DIR_LENGTH) return null;

      return { t: "fire", tick, renderTick, origin, dir };
    }

    case "grenadeArm": {
      const iid = x["iid"];
      if (!isCleanString(iid, MAX_ID_LENGTH) || iid.length === 0) return null;
      return { t: "grenadeArm", iid };
    }

    case "grenadeThrow": {
      const tick = x["tick"];
      if (!isSafeInt(tick) || tick < 0) return null;
      const origin = validateVec3(x["origin"], MAX_ABS_COORD);
      if (origin === null) return null;
      const dir = validateVec3(x["dir"], MAX_ABS_COORD);
      if (dir === null) return null;
      const len = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
      if (len < MIN_DIR_LENGTH || len > MAX_DIR_LENGTH) return null;
      return { t: "grenadeThrow", tick, origin, dir };
    }

    case "respawn":
      return { t: "respawn" };

    case "queueJoin":
      return { t: "queueJoin" };

    case "queueLeave":
      return { t: "queueLeave" };

    case "buy": {
      // `opId` shares invOp's counter and bound; strict increase is the
      // server's bookkeeping.
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      const traderId = x["traderId"];
      if (!isCleanString(traderId, MAX_CONTENT_ID_LENGTH) || traderId.length === 0) return null;
      const defId = x["defId"];
      if (!isCleanString(defId, MAX_CONTENT_ID_LENGTH) || defId.length === 0) return null;
      return { t: "buy", opId, traderId, defId };
    }

    case "sell": {
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      const traderId = x["traderId"];
      if (!isCleanString(traderId, MAX_CONTENT_ID_LENGTH) || traderId.length === 0) return null;
      // An INSTANCE id, not a content id: server-minted and short.
      const iid = x["iid"];
      if (!isCleanString(iid, MAX_ID_LENGTH) || iid.length === 0) return null;
      return { t: "sell", opId, traderId, iid };
    }

    case "repair": {
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      // An INSTANCE id, not a content id: server-minted and short. No trader is
      // named at all — a repair is a service rather than a trade.
      const iid = x["iid"];
      if (!isCleanString(iid, MAX_ID_LENGTH) || iid.length === 0) return null;
      return { t: "repair", opId, iid };
    }

    case "questTurnIn": {
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      // A CONTENT id: quest ids are authored, exactly like a def id.
      const questId = x["questId"];
      if (!isCleanString(questId, MAX_CONTENT_ID_LENGTH) || questId.length === 0) return null;
      // Instance ids, a bounded list, rebuilt element by element. Whether they
      // name anything is the session's answer.
      const iids = x["iids"];
      if (!Array.isArray(iids)) return null;
      if (iids.length < 1 || iids.length > MAX_TURN_IN_IIDS) return null;
      const out: string[] = [];
      for (const iid of iids) {
        if (!isCleanString(iid, MAX_ID_LENGTH) || iid.length === 0) return null;
        out.push(iid);
      }
      return { t: "questTurnIn", opId, questId, iids: out };
    }

    // --- The hideout: an `opId`, a name and at most one bounded number each.
    // Whether the name means anything is the hideout's own answer.

    case "hideoutBuild": {
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      // A CONTENT id, authored rather than minted, exactly like `defId`.
      const moduleId = x["moduleId"];
      if (!isCleanString(moduleId, MAX_CONTENT_ID_LENGTH) || moduleId.length === 0) return null;
      return { t: "hideoutBuild", opId, moduleId };
    }

    case "hideoutCraft":
    case "hideoutCollect": {
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      const craftId = x["craftId"];
      if (!isCleanString(craftId, MAX_CONTENT_ID_LENGTH) || craftId.length === 0) return null;
      return x["t"] === "hideoutCraft"
        ? { t: "hideoutCraft", opId, craftId }
        : { t: "hideoutCollect", opId, craftId };
    }

    case "hideoutHarvest": {
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      const moduleId = x["moduleId"];
      if (!isCleanString(moduleId, MAX_CONTENT_ID_LENGTH) || moduleId.length === 0) return null;
      return { t: "hideoutHarvest", opId, moduleId };
    }

    case "hideoutFuel": {
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      // An INSTANCE id — the canister in the stash — so the shorter bound.
      const iid = x["iid"];
      if (!isCleanString(iid, MAX_ID_LENGTH) || iid.length === 0) return null;
      return { t: "hideoutFuel", opId, iid };
    }

    case "hideoutPower": {
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      // A switch is a switch: a truthy string is not an "on".
      const on = x["on"];
      if (typeof on !== "boolean") return null;
      return { t: "hideoutPower", opId, on };
    }

    case "hideoutTrain": {
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      // A CONTENT id — a skill is authored, not minted — so the same bound as
      // a module id. Whether the gym teaches it is `trainProblem`'s answer.
      const skill = x["skill"];
      if (!isCleanString(skill, MAX_CONTENT_ID_LENGTH) || skill.length === 0) return null;
      // Bounded rather than clamped: a score outside [0, 1] is not a rounding
      // error, it is a client sending something it was never given.
      const score = x["score"];
      if (typeof score !== "number" || !Number.isFinite(score)) return null;
      if (score < 0 || score > 1) return null;
      return { t: "hideoutTrain", opId, skill, score };
    }

    // --- Friends and the party: an `opId`, a username and a boolean. Whether
    // the name is a real account, a friend, online — social handlers' answers.

    case "friendRequest":
    case "friendRemove":
    case "partyInvite":
    case "partyKick": {
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      const username = x["username"];
      if (!isValidUsername(username)) return null;
      switch (x["t"]) {
        case "friendRequest":
          return { t: "friendRequest", opId, username };
        case "friendRemove":
          return { t: "friendRemove", opId, username };
        case "partyInvite":
          return { t: "partyInvite", opId, username };
        default:
          return { t: "partyKick", opId, username };
      }
    }

    case "friendRespond": {
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      const username = x["username"];
      if (!isValidUsername(username)) return null;
      // A real boolean: a truthy string is not an acceptance.
      const accept = x["accept"];
      if (typeof accept !== "boolean") return null;
      return { t: "friendRespond", opId, username, accept };
    }

    case "partyRespond": {
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      // No username: this answers THE pending invite, whichever it is.
      const accept = x["accept"];
      if (typeof accept !== "boolean") return null;
      return { t: "partyRespond", opId, accept };
    }

    case "partyLeave": {
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      return { t: "partyLeave", opId };
    }

    case "invOp": {
      // `opId` is only bounded here; strict increase is per-connection state
      // the server tracks.
      const opId = x["opId"];
      if (!isSafeInt(opId) || opId < 0) return null;
      const op = validateInventoryOp(x["op"]);
      if (op === null) return null;
      return { t: "invOp", opId, op };
    }

    case "open":
    case "close": {
      const containerId = x["containerId"];
      if (!isCleanString(containerId, MAX_ID_LENGTH) || containerId.length === 0) return null;
      return { t: x["t"] === "open" ? "open" : "close", containerId };
    }

    case "pickup": {
      const itemId = x["itemId"];
      if (!isCleanString(itemId, MAX_ID_LENGTH) || itemId.length === 0) return null;
      return { t: "pickup", itemId };
    }

    case "useMed": {
      const iid = x["iid"];
      if (!isCleanString(iid, MAX_ID_LENGTH) || iid.length === 0) return null;
      return { t: "useMed", iid };
    }

    case "glass": {
      // A pane id is an INDEX into `map.glass`; the bound keeps a client from
      // naming a position no map could hold.
      const id = x["id"];
      if (!isSafeInt(id) || id < 0 || id > 1_000_000) return null;
      const act = x["act"];
      if (act !== "smash" && act !== "climb") return null;
      return { t: "glass", id, act };
    }

    // No payload to check: the check is about whatever gun the server already
    // knows is in this player's hands.
    case "magCheck":
      return { t: "magCheck" };

    default:
      return null;
  }
}
